import {
  type CreateQueryStatusRequest,
  type QueryStatusDetail,
  QueryStatusDetailSchema,
  type QueryStatusListQuery,
  type QueryStatusListResponse,
  QueryStatusListResponseSchema,
  type QueryStatusOption,
  QueryStatusOptionListSchema,
  type QueryStatusOptionsQuery,
  type UpdateQueryStatusRequest,
} from '@utazzo/shared';

import { apiClient } from '@/lib/api-client';

/**
 * Query Status master API. Every response is validated with the shared Zod
 * schema so a server change can't sneak past the UI types.
 */

export const listQueryStatuses = async (
  query: QueryStatusListQuery,
): Promise<QueryStatusListResponse> => {
  const { data } = await apiClient.get<unknown>('/query-statuses', { params: query });
  return QueryStatusListResponseSchema.parse(data);
};

export const listQueryStatusOptions = async (
  query?: QueryStatusOptionsQuery,
): Promise<QueryStatusOption[]> => {
  const { data } = await apiClient.get<unknown>('/query-statuses/options', { params: query });
  return QueryStatusOptionListSchema.parse(data);
};

export const getQueryStatus = async (id: string): Promise<QueryStatusDetail> => {
  const { data } = await apiClient.get<unknown>(`/query-statuses/${id}`);
  return QueryStatusDetailSchema.parse(data);
};

export const createQueryStatus = async (
  payload: CreateQueryStatusRequest,
): Promise<QueryStatusDetail> => {
  const { data } = await apiClient.post<unknown>('/query-statuses', payload);
  return QueryStatusDetailSchema.parse(data);
};

export const updateQueryStatus = async (
  id: string,
  payload: UpdateQueryStatusRequest,
): Promise<QueryStatusDetail> => {
  const { data } = await apiClient.patch<unknown>(`/query-statuses/${id}`, payload);
  return QueryStatusDetailSchema.parse(data);
};

export const deleteQueryStatus = async (id: string): Promise<void> => {
  await apiClient.delete(`/query-statuses/${id}`);
};

export const bulkDeleteQueryStatuses = async (
  ids: string[],
): Promise<{ updated: number; failed: { id: string; error: string }[] }> => {
  const { data } = await apiClient.post<{
    updated: number;
    failed: { id: string; error: string }[];
  }>('/query-statuses/bulk-delete', { ids });
  return data;
};

export const bulkSetQueryStatusesStatus = async (
  ids: string[],
  isActive: boolean,
): Promise<{ updated: number; failed: { id: string; error: string }[] }> => {
  const { data } = await apiClient.post<{
    updated: number;
    failed: { id: string; error: string }[];
  }>('/query-statuses/bulk-set-status', { ids, isActive });
  return data;
};
